// Zod schema validating a framework content artifact - shape AND structural
// rules that hold for ANY WAR (Databricks, Applied AI, ...). Each catalog entry
// is parsed through this at import time, so malformed content fails fast (and
// fails CI). WAR-specific expectations (e.g. Databricks' exact pillar set and
// question counts) are asserted in that WAR's own content test, not here.

import { z } from 'zod';

const riskSchema = z.enum(['High', 'Medium', 'Low']);
const categorySchema = z.enum(['Core', 'Lens']);

const questionSchema = z.object({
  // "OPS-01" (Well-Architected) or "DEF-S01"/"DEF-T01" (strategic/tactical layers).
  id: z.string().regex(/^[A-Z]+-[A-Z]?\d{2}$/, 'question id must look like "OPS-01" or "DEF-S01"'),
  pillarId: z.string().min(1),
  category: categorySchema,
  question: z.string().min(1),
  whatGoodLooksLike: z.string().min(1),
  risk: riskSchema,
  // Optional guidance: some frameworks (e.g. the AI Maturity reviews) omit a
  // per-question rationale, a remediation step, or an external reference.
  intent: z.string().min(1).optional(),
  remediation: z.string().min(1).optional(),
  reference: z.string().min(1).optional(),
});

const pillarSchema = z.object({
  id: z.string().regex(/^[A-Z][A-Z0-9]*$/, 'pillar id must be uppercase, e.g. "OPS"'),
  name: z.string().min(1),
  category: categorySchema,
  description: z.string().min(1),
});

/** Databricks WAR's expected question counts - asserted in its content test. */
export const EXPECTED_CORE_QUESTIONS = 39;
export const EXPECTED_LENS_QUESTIONS = 18;
export const EXPECTED_TOTAL_QUESTIONS = EXPECTED_CORE_QUESTIONS + EXPECTED_LENS_QUESTIONS;

export const frameworkSchema = z
  .object({
    focus: z.string().regex(/^[a-z][a-z0-9-]*$/, 'focus must be a slug, e.g. "databricks"'),
    title: z.string().min(1),
    tagline: z.string().min(1),
    branding: z
      .object({ vendor: z.string().min(1).optional(), referenceLabel: z.string().min(1).optional() })
      .optional(),
    terms: z
      .object({
        unit: z.string().min(1),
        unitPlural: z.string().min(1),
        coreGroupLabel: z.string().min(1),
        lensGroupLabel: z.string().min(1),
        coreItemLabel: z.string().min(1),
        lensItemLabel: z.string().min(1),
      })
      .partial()
      .optional(),
    pillars: z.array(pillarSchema).min(1),
    questions: z.array(questionSchema).min(1),
  })
  .superRefine((fw, ctx) => {
    const pillarById = new Map(fw.pillars.map((p) => [p.id, p]));
    if (pillarById.size !== fw.pillars.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate pillar id' });
    }

    // Question integrity: unique ids, id prefix matches pillar, category matches pillar.
    const seen = new Set<string>();
    for (const q of fw.questions) {
      if (seen.has(q.id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate question id ${q.id}` });
      }
      seen.add(q.id);

      if (q.id.split('-')[0] !== q.pillarId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `question ${q.id} prefix != pillar ${q.pillarId}` });
      }
      const pillar = pillarById.get(q.pillarId);
      if (!pillar) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `question ${q.id} references unknown pillar` });
      } else if (pillar.category !== q.category) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `question ${q.id} category != pillar category` });
      }
    }
  });
